/* eslint-disable */
import React, { PureComponent, Fragement } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import isEqual from 'lodash-es/isEqual'
import Table from '../common/Table'
import zIndex from '../../utils/zIndexScale'

const LEFT = 'left'
const CENTER = 'center'

const TableWrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
`

const ScrollTable = styled(Table)`
  tr {
    padding: 10px 0px;
    border: none;
  }
`

const Thead = styled.thead`
  background: ${({ theme }) => theme.exchangeSecondBg};
  background-size: auto 100%;
  color: ${({ theme }) => theme.thColor};
  tr {
    font-size: 12px;
    color: ${({ theme }) => {
      theme.exchangeTableTitle
    }};
  }
  th {
    background-color: inherit;
    word-break: break-all;
    text-transform: capitalize;
  }
  .showSortMark {
    cursor: pointer;
    i {
      display: none;
    }
  }
  .sortHd {
    i {
      display: inline-block;
    }
  }
`

const Tbody = styled.tbody`
  tr {
    position: relative;

    &:hover {
      background-color: ${({ theme }) => theme.tableRowHoverBg};

      td {
        color: ${({ theme }) => theme.tableRowHoverTextColor};
      }
    }
  }

  td {
    position: relative;
    text-align: center;
    white-space: nowrap;
    cursor: pointer;
    font-size: 12px;
    color: ${({ theme }) => theme.fontColor};
  }
  td.tr-shadow {
    position: absolute;
    right: 0;
    height: 16px;
    width: 0;
    background: ${props => props.bg};
  }
`

const IconWrap = styled.span`
  transition: transform 0.3s ease-in;
  transform: ${({ isUp }) => (isUp ? 'rotate(180deg) translateY(1px)' : 'rotate(0deg)')};
`

const HeaderLeft = styled.div`
  display: flex;
  justify-content: flex-start;
  white-space: nowrap;
  user-select: none;
`

const HeaderRight = styled.div`
  display: flex;
  justify-content: flex-end;
  white-space: nowrap;
  user-select: none;
`

const HeaderCenter = styled.div`
  display: flex;
  justify-content: center;
  white-space: nowrap;
`

const FixedTableHead = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 5px;
  z-index: ${zIndex.fixedTableHeader};
  thead tr th {
    &:first-child {
      padding-left: 5px;
    }
  }
`

const FixedTableBody = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  position: absolute;
  width: 100%;
  top: 20px;
  bottom: 5px;
  tr td {
    &:last-child {
      padding-right: 4px;
    }
    &:first-child {
      padding-left: 5px;
    }
  }
  ::-webkit-scrollbar {
    width: 6px;
    border-radius: 2px;
    background-color: ${({ theme }) => theme.scrollbarBg};
  }

  ::-webkit-scrollbar-track {
    border-radius: 2px;
    background-color: ${({ theme }) => theme.scrollbarTrackColor};
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 2px;
    background-color: ${({ theme }) => theme.scrollbarThumbColor};
  }

  ::-webkit-scrollbar-corner {
    background-color: ${({ theme }) => theme.scrollbarThumbColor};
  }
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.scrollbarThumbColor} ${({ theme }) => theme.scrollbarBg};
`

class ScrollableTable extends PureComponent {
  static propTypes = {
    dataSource: PropTypes.array,
    columns: PropTypes.array.isRequired,
    onRowClick: PropTypes.func,
    isFixedHeader: PropTypes.bool,
    hideHeader: PropTypes.bool,
    withShadow: PropTypes.bool,
    shadowBg: PropTypes.string,
    shadowDataIndex: PropTypes.string,
    rowKey: PropTypes.string,
    median: PropTypes.number,
    onScroll: PropTypes.func,
    sort: PropTypes.func,
  }

  static defaultProps = {
    dataSource: [],
    onRowClick: null,
    paging: null,
    isFixedHeader: false,
    hideHeader: false,
    withShadow: false,
    shadowBg: '',
    rowKey: '',
    onScroll: () => {},
    shadowDataIndex: '',
    sort: () => {},
    median: Math.pow(10, 3),
  }

  constructor(props) {
    super(props)
    this.state = {
      data: props.dataSource,
      columns: props.columns,
    }
  }

  componentDidUpdate(prevProps) {
    const { dataSource, columns } = this.props

    if (JSON.stringify(columns) !== JSON.stringify(prevProps.columns)) {
      this.setState({ columns: columns, data: dataSource })
      return
    }

    if (!isEqual(dataSource, prevProps.dataSource)) {
      this.setState({ data: dataSource })
    }
  }

  onRowClick = rowData => {
    const { onRowClick } = this.props
    onRowClick && onRowClick(rowData)
  }

  _getColGroup() {
    const { isFixedHeader, columns } = this.props
    if (isFixedHeader) {
      const cols = columns.map((item, index) => <col style={{ width: item.width }} key={index} />)
      return cols
    }
    return <Fragement />
  }

  _renderHeaderDetails(title, sortType) {
    const headers = [<span key={title}>{title}</span>]
    if (sortType) {
      headers.push(
        <IconWrap key="iconWrap" isUp={sortType === 'sort_asc'}>
          ▾
        </IconWrap>
      )
    }
    return headers
  }

  _renderSortedHeader(headerAlign, title, sortType) {
    if (headerAlign === LEFT) {
      return <HeaderLeft>{this._renderHeaderDetails(title, sortType)}</HeaderLeft>
    }
    if (headerAlign === CENTER) {
      return <HeaderCenter>{this._renderHeaderDetails(title, sortType)}</HeaderCenter>
    }
    return <HeaderRight>{this._renderHeaderDetails(title, sortType)}</HeaderRight>
  }

  _renderHeader(headerAlign, title) {
    if (headerAlign === LEFT) {
      return <HeaderLeft>{title}</HeaderLeft>
    }
    if (headerAlign === CENTER) {
      return <HeaderCenter>{title}</HeaderCenter>
    }
    return <HeaderRight>{title}</HeaderRight>
  }

  _renderTD(headerAlign, value) {
    if (headerAlign === LEFT) {
      return <HeaderLeft>{value}</HeaderLeft>
    }
    if (headerAlign === CENTER) {
      return <HeaderCenter>{value}</HeaderCenter>
    }
    return <HeaderRight>{value}</HeaderRight>
  }

  generateHead = () => {
    const { columns } = this.state
    const theads = columns.map((item, index) => {
      if (item.sorter) {
        const className = !item.isShowSortMark ? 'showSortMark' : 'sortHd'
        return (
          <th
            key={item.key || item.dataIndex || index}
            data-key={item.dataIndex}
            className={className}
            onClick={() => {
              this.handleHeadClick(item.dataIndex)
            }}
            style={{ width: item.width }}
          >
            {this._renderSortedHeader(item.headerAlign, item.title, item.sortType)}
          </th>
        )
      }
      return (
        <th key={item.key || item.dataIndex || index}>
          {this._renderHeader(item.headerAlign, item.title)}
        </th>
      )
    })

    return <tr>{theads}</tr>
  }

  isDataShow = key => {
    const { columns } = this.state
    for (let i = 0, len = columns.length; i < len; i++) {
      if (columns[i].dataIndex === key) {
        return true
      }
    }
    return false
  }

  generateBody = () => {
    const { columns, data } = this.state
    const { withShadow, shadowBg, shadowDataIndex, median } = this.props
    const tbody = data.map((item, trIndex) => {
      const tdArr = []
      Object.keys(item).forEach(key => {
        if (this.isDataShow(key)) {
          tdArr.push(item[key])
        }
      })

      const tds = tdArr.map((value, index) => {
        const thCol = columns[index]
        if (thCol && thCol.render) {
          return <td key={index}>{thCol.render(item, trIndex)}</td>
        }
        return <td key={index}>{this._renderTD(thCol.headerAlign, value)}</td>
      })

      // const totalAmount = data.reduce((prev, dataItem) => (prev + parseFloat(dataItem[shadowDataIndex])), 0)
      const shadowWidth = parseFloat(item[shadowDataIndex]) / median

      return (
        <tr
          key={trIndex}
          onClick={() => {
            this.onRowClick(item)
          }}
        >
          {tds}
          {withShadow && (
            <td
              className="tr-shadow"
              style={{ background: shadowBg, width: `${Math.min(shadowWidth, 1) * 100}%` }}
            ></td>
          )}
        </tr>
      )
    })

    return tbody || []
  }

  handleHeadClick = dataIndex => {
    let { columns } = this.state
    const { sort } = this.props
    let sortType
    let sortFunc
    const newColumns = []
    columns.forEach(item => {
      const col = { ...item }
      if (col.dataIndex === dataIndex) {
        sortFunc = col.sortFunc
        if (!col.sortType) {
          col.sortType = 'sort_asc'
          sortType = 'sort_asc'
        } else {
          sortType = col.sortType === 'sort_asc' ? 'sort_desc' : 'sort_asc'
          col.sortType = col.sortType === 'sort_asc' ? 'sort_desc' : 'sort_asc'
        }
      } else {
        col.sortType = ''
      }
      newColumns.push(col)
    })

    console.log('sortType: ' + sortType)

    sort && sort(sortType, dataIndex, sortFunc)

    this.setState({ columns: newColumns })
  }

  _onFixedScroll = () => {
    const { isFixedHeader } = this.props
    // only fix table can scroll
    if (!isFixedHeader) return
  }

  generateFixedTable = () => {
    const { hideHeader } = this.props
    const colGroup = this._getColGroup()
    const thead = this.generateHead()
    const tbody = this.generateBody()
    return (
      <div>
        <FixedTableHead>
          <ScrollTable>
            <colgroup>{colGroup}</colgroup>
            {!hideHeader && <Thead>{thead}</Thead>}
          </ScrollTable>
        </FixedTableHead>
        <FixedTableBody className="fixed-table" onScroll={this._onFixedScroll}>
          <ScrollTable>
            <colgroup>{colGroup}</colgroup>
            <Tbody>{tbody}</Tbody>
          </ScrollTable>
        </FixedTableBody>
      </div>
    )
  }

  generateTable = () => {
    const { hideHeader } = this.props
    const thead = this.generateHead()
    const tbody = this.generateBody()
    return (
      <ScrollTable>
        {!hideHeader && <Thead>{thead}</Thead>}
        <Tbody>{tbody}</Tbody>
      </ScrollTable>
    )
  }

  render() {
    const { isFixedHeader } = this.props
    const content = isFixedHeader ? this.generateFixedTable() : this.generateTable()
    return <TableWrap className="table-wrap">{content}</TableWrap>
  }
}

export default ScrollableTable
